<template>
  <div v-bind:key="viewKey">
    <!--Breadcrumb button-->
    <breadcrumb-button hide-settings>
      <template #beforeDropdown>
        <div>
          <b-button variant="success" class="btn-icon" v-b-tooltip v-bind:title="$t('operations.add')" v-if="hasPermission('CLIENT_CREATE')"
                    @click="onClientSelected(null)"
          >
            <i class="fa-solid fa-user-plus"></i>
          </b-button>
          <b-button variant="primary" class="btn-icon mx-1" v-b-tooltip v-bind:title="$t('columns.self')"
                    @click="openColumnsModal"
          >
            <i class="fa-solid fa-bars"></i>
          </b-button>
        </div>
      </template>
    </breadcrumb-button>
    <!--Filters-->
    <filter-card :filter-key="viewKey" v-model="filter" v-bind:fields="filterFields"
                 v-bind:hidden-fields="{'buTypes': viewKey!=='clients' }" search
                 file-export @export="createExcelFile" file-import @import="showImportExcelModal"
    />
    <!--Table Card-->
    <b-card no-body>

      <!--      <b-card-header class="pb-50">
              <h5>{{$t("menu.clients")}}</h5>
              <b-button @click="createExcelFile" class="d-inline-block" size="sm" variant="outline-success">
                Export this page to excel
              </b-button>
            </b-card-header>-->
      <b-card-body>
        <!--Table-->
        <clients-table ref="clientTable" v-bind:filters="filter" v-bind:columns="columns" :has-selection="true"
                       @selected="onClientSelected" :lazy="true" :busy="loading"
        >
          <!-- 3 buttons to change busUnit, status, representative -->
          <template #table-top-actions="{ selectedIds }">
            <div v-if="Object.keys(selectedIds).length > 0" class="w-100 mb-1">

              <!-- STEP HEADER -->
              <div class="d-flex align-items-center mb-1">
                <span class="mr-1 small text-muted">Selected: {{ Object.keys(selectedIds).length }}</span>

                <b-badge :variant="currentStep >= 1 ? 'primary' : 'light'" class="mr-1">1. Action</b-badge>
                <b-badge :variant="currentStep >= 2 ? 'primary' : 'light'" class="mr-1">2. Details</b-badge>
                <b-badge :variant="currentStep >= 3 ? 'primary' : 'light'">3. Apply</b-badge>

                <b-button size="sm" class="ml-auto" variant="outline-secondary" @click="resetBulkAction">
                  <i class="fa-solid fa-rotate-left mr-1"></i>Reset
                </b-button>
              </div>

              <!-- STEP 1: PICK ACTION -->
              <div class="d-flex align-items-center flex-wrap mb-1">
                <b-form-group class="mb-0 mr-1">
                  <label class="mb-0 small text-muted">{{$t('operations.chooseAction')}}</label>
                  <b-form-select v-model="bulkAction" :options="bulkActionOptions" class="min-w-220"
                                 @change="onPickAction"
                  />
                </b-form-group>

                <!-- live hint -->
                <transition name="fade">
                  <small v-if="bulkAction" class="text-muted ml-2">
                    {{ actionHint }}
                  </small>
                </transition>
              </div>

              <!-- STEP 2: DETAILS (conditional pickers) -->
              <transition name="fade">
                <div v-if="currentStep >= 2 && bulkAction !== 'DELETE_COMMENTS'" class="mt-1">
                  <!-- Set Representative -->
                  <div v-if="bulkAction === 'SET_REP'">
                    <label class="small text-muted d-block mb-1">Select representative</label>
                    <user-select-box v-model="optionSelection"
                                     :organizations="filter.orgIds" :business-units="filter.buIds"/>
                  </div>

                  <!-- Set Random Representative (multi) -->
                  <div v-else-if="bulkAction === 'SET_RANDOM_REP'">
                    <label class="small text-muted d-block mb-1">Pick allowed representatives (pool)</label>
                    <user-select-box :multi="true" v-model="optionSelectionMulti"
                                     :organizations="filter.orgIds" :business-units="filter.buIds"/>
                    <small class="text-muted d-block mt-1">
                      A random user will be assigned from this pool for each selected client.
                    </small>
                  </div>

                  <!-- Change Status -->
                  <div v-else-if="bulkAction === 'CHANGE_STATUS'">
                    <label class="small text-muted d-block mb-1">Select status</label>
                    <!-- If you already have <client-status-picker> keep it; otherwise the dummy select below helps you wire later -->
                    <client-status-picker :multiple="false" v-model="optionSelection"/>
                  </div>

                  <!-- Change Business Unit -->
                  <div v-else-if="bulkAction === 'CHANGE_BU'">
                    <label class="small text-muted d-block mb-1">Select business unit</label>
                    <business-unit-select-box v-model="optionSelection"/>
                  </div>

                  <div v-else-if="bulkAction === 'CHANGE_BU'">
                    <label class="small text-muted d-block mb-1">Select business unit</label>
                    <business-unit-select-box v-model="optionSelection"/>
                  </div>

                  <div v-else-if="bulkAction === 'DUPLICATE_TO_ORG'" class="duplicate-to-org">
                    <!-- Step 1: organization (required) -->
                    <label class="small text-muted d-block mb-1">Target organization</label>
                    <organization-select-box v-model="dupOrgId" @input="onDupOrgChange"/>

                    <!-- Step 2: branch (required, gated on org).
                         We use v-select directly here instead of
                         BusinessUnitSelectBox because that wrapper's
                         validateFilter() is multi-select-only and can corrupt
                         the single-string v-model when the org changes. -->
                    <transition name="fade">
                      <div v-if="dupOrgId" class="mt-2">
                        <label class="small text-muted d-block mb-1">Target branch</label>
                        <v-select
                            v-model="dupBuId"
                            :options="dupBuOptions"
                            :reduce="bu => bu.id"
                            label="name"
                            :clearable="true"
                            @input="onDupBuChange"
                        >
                          <template #option="opt">
                            {{ opt.name }} <span class="text-muted small">— {{ opt.type }}</span>
                          </template>
                        </v-select>
                      </div>
                    </transition>

                    <!-- Step 3: representative (optional, gated on BU, filtered to BU) -->
                    <transition name="fade">
                      <div v-if="dupBuId" class="mt-2">
                        <label class="small text-muted d-block mb-1">Representative <span class="text-muted">(optional)</span></label>
                        <user-select-box
                            v-model="dupRepId"
                            :business-unit-id="dupBuId"
                            :clearable="true"
                        />
                      </div>
                    </transition>

                    <!-- Step 4: notes choice (optional, gated on BU) -->
                    <transition name="fade">
                      <div v-if="dupBuId" class="mt-2">
                        <label class="small text-muted d-block mb-1">Client notes</label>
                        <b-button-group size="sm">
                          <b-button
                              :variant="dupCopyNotes === false ? 'danger' : 'outline-danger'"
                              @click="dupCopyNotes = false">
                            <i class="fa-solid fa-trash-can mr-1"></i>Don't copy
                          </b-button>
                          <b-button
                              :variant="dupCopyNotes === true ? 'success' : 'outline-success'"
                              @click="dupCopyNotes = true">
                            <i class="fa-solid fa-clone mr-1"></i>Copy notes
                          </b-button>
                        </b-button-group>
                      </div>
                    </transition>

                    <!-- Step 5: status (optional, gated on BU) -->
                    <transition name="fade">
                      <div v-if="dupBuId" class="mt-2">
                        <label class="small text-muted d-block mb-1">Initial status <span class="text-muted">(defaults to NEW)</span></label>
                        <client-status-picker v-model="dupStatusId" :multiple="false"/>
                      </div>
                    </transition>
                  </div>
                </div>
              </transition>

              <!-- STEP 3: APPLY -->
              <transition name="fade">
                <div v-if="currentStep >= 2" class="mt-2 d-flex align-items-center flex-wrap">
                  <b-button size="sm"
                            variant="primary"
                            class="mr-1"
                            :disabled="!isReadyToApply || duplicating"
                            @click="applyBulkAction"
                  >
                    <b-spinner v-if="duplicating" small class="mr-1"/>
                    <i v-else class="fa-solid fa-check mr-1"></i>
                    {{ duplicating
                      ? $t('clientsBulk.duplicating', { done: duplicateDone, total: duplicateTotal })
                      : $t('operations.apply') }}
                  </b-button>

                  <b-button size="sm" variant="outline-secondary" @click="resetTargets" :disabled="duplicating">
                    Clear selection
                  </b-button>

                  <!-- Inline progress bar — only visible while a duplicate-to-org
                       run is in flight. Locks the user out of re-firing the
                       action (button is also disabled) and gives explicit %. -->
                  <div v-if="duplicating && bulkAction === 'DUPLICATE_TO_ORG'"
                       class="ml-2 flex-grow-1" style="min-width: 200px; max-width: 400px;">
                    <b-progress :value="duplicateProgress" :max="100"
                                show-progress animated variant="primary" height="14px"/>
                  </div>

                  <transition name="fade">
                    <small v-if="applyError" class="text-danger ml-2">{{ applyError }}</small>
                  </transition>
                </div>
              </transition>

            </div>
          </template>
        </clients-table>
      </b-card-body>
    </b-card>
    <!--Modal-->
    <client-modal-basic ref="client-modal-basic" @next-client="showNextClientsSequentially" @closed="onClientModalBasicClosed"/>
    <columns-modal ref="columns-modal" column-key="clients" v-model="columns" width-allowed
                   v-bind:columns-names="columnsNames" v-bind:columns-default="columnsDefaultNames"
    />
    <b-modal ref="fileUploadModal" title="Import Excel File" :ok-disabled="!isFormValid"
             @ok="importExcelFile" no-close-on-backdrop ok-variant="success" cancel-variant="danger"
    >
      <b-row>
        <b-col cols="12">
          <b-button style="float: right" variant="primary" size="sm" @click="downloadExcelTemplate">
            {{ $t('operations.downloadExcelTemplate') }}
          </b-button>
        </b-col>
        <b-col cols="12" >
          <span style="font-size: 12px">{{ $t('operations.pickBusinessUnit') }}</span>
          <business-unit-select-box class="small" v-model="selectedBranchExcel"/>
        </b-col>
        <b-col cols="12" class="mt-1">
          <span style="font-size: 12px">{{ $t('columns.representativeUserId') }}</span>
          <user-select-box class="small" v-model="selectedUserExcel" :business-unit-id="selectedBranchExcel"/>
        </b-col>

        <b-col cols="12" class="mt-3">
          <b-form-file v-model="file" accept=".xls,.xlsx"></b-form-file>
        </b-col>
      </b-row>
    </b-modal>

    <!--
      Duplicate-to-org report modal.
      Shown after every bulk duplicate run. Two sections:
        - Successes: list of newly created clients (email).
        - Failures: list of source clients that couldn't be duplicated,
                    each with the backend-reported reason.
      Counts in the section headers come from the live arrays so the modal
      doesn't lie if a future change updates them mid-render.
    -->
    <b-modal v-model="showDuplicateReport"
             :title="$t('clientsBulk.duplicateReport.title')"
             size="lg"
             ok-only
             :ok-title="$t('operations.close')"
             ok-variant="primary"
             no-close-on-backdrop
    >
      <div class="mb-3 d-flex align-items-center">
        <b-badge variant="success" class="mr-2">
          <i class="fa-solid fa-check mr-1"></i>
          {{ $t('clientsBulk.duplicateReport.successCount', { count: duplicateSuccesses.length }) }}
        </b-badge>
        <b-badge variant="danger">
          <i class="fa-solid fa-xmark mr-1"></i>
          {{ $t('clientsBulk.duplicateReport.failureCount', { count: duplicateFailures.length }) }}
        </b-badge>
      </div>

      <!-- Successes -->
      <div v-if="duplicateSuccesses.length" class="mb-3">
        <h6 class="text-success mb-1">
          <i class="fa-solid fa-circle-check mr-1"></i>
          {{ $t('clientsBulk.duplicateReport.successesHeading') }}
        </h6>
        <div class="border rounded" style="max-height: 220px; overflow-y: auto;">
          <b-list-group flush>
            <b-list-group-item v-for="(c, i) in duplicateSuccesses" :key="'ds-' + (c.id || i)"
                               class="py-1 small">
              <i class="fa-solid fa-user text-success mr-1"></i>
              <span>{{ c.email || c.id }}</span>
            </b-list-group-item>
          </b-list-group>
        </div>
      </div>

      <!-- Failures -->
      <div v-if="duplicateFailures.length">
        <h6 class="text-danger mb-1">
          <i class="fa-solid fa-circle-exclamation mr-1"></i>
          {{ $t('clientsBulk.duplicateReport.failuresHeading') }}
        </h6>
        <div class="border rounded" style="max-height: 260px; overflow-y: auto;">
          <b-list-group flush>
            <b-list-group-item v-for="(f, i) in duplicateFailures"
                               :key="'df-' + (f.clientId || i)"
                               class="py-1 small">
              <div class="d-flex justify-content-between">
                <div>
                  <i class="fa-solid fa-user-xmark text-danger mr-1"></i>
                  <strong>{{ f.email || f.clientId }}</strong>
                </div>
                <span class="text-muted">{{ f.clientId }}</span>
              </div>
              <div class="text-danger small mt-1" style="word-break: break-word;">
                {{ f.reason || $t('clientsBulk.duplicateReport.unknownReason') }}
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>
      </div>

      <div v-if="!duplicateSuccesses.length && !duplicateFailures.length"
           class="text-center text-muted py-3">
        {{ $t('clientsBulk.duplicateReport.empty') }}
      </div>
    </b-modal>
  </div>
</template>

<script>

import { mapGetters, mapActions } from 'vuex';
import ClientsTable from '@/components/tables/ClientsTable';
import BreadcrumbButton from '@/components/widget/breadcrumbButton';
import ClientSearchRequest from '@/model/client/ClientSearchRequest';
import ColumnsModal from '@/components/modal/columnsModal';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinBase from '@/components/mixin/mixinBase';
import FilterCard from '@/components/widget/FilterCard';
import ClientFilterFields from '@/model/filter/ClientFilterFields';
import BusinessUnitSelectBox from '@/components/widget/select/BusinessUnitSelectBox';
import ClientStatusPicker from '@/components/widget/picker/clientStatusPicker';
import UserPicker from '@/components/widget/picker/userPicker';
import { getClientFields, getDefaultFilter } from '@/model/client/ClientColumns';
import mixinPopover from '@/components/mixin/mixinPopover';
import UserSelectBox from '@/components/widget/select/UserSelectBox';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import ClientModalBasic from '@/components/modal/clientModalBasic.vue'
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox.vue'
import permissions from '@/model/Permissions'

export default {
  name: 'ClientsView',
  components: {
    OrganizationSelectBox,
    ClientModalBasic,
    UserSelectBox,
    ClientStatusPicker,
    UserPicker,
    BusinessUnitSelectBox,
    FilterCard,
    ColumnsModal,
    BreadcrumbButton,
    ClientsTable
  },

  mixins: [mixinBase, mixinPermissions, mixinPopover, mixinNotifications],
  data() {
    return {
      loading: true,
      optionSelection: '',
      optionSelectionMulti: null,
      columnsNames: getClientFields(),
      columnsDefaultNames: getDefaultFilter(),
      filter: ClientSearchRequest(),
      filterLoaded: false,
      columns: [],
      filterFields: [],
      popoverShowBU: false,
      popoverShowStatus: false,
      popoverShowRepresentative: false,
      popoverShowRepresentativeRandom: false,
      selectedBranchExcel: null,
      selectedUserExcel: null,
      // Duplicate-to-org wizard state. Reset by resetTargets() when the user
      // changes the action or the action completes.
      dupOrgId: null,
      dupBuId: null,
      dupRepId: null,
      dupStatusId: null,
      dupCopyNotes: false,
      // Duplicate-to-org execution state. While `duplicating` is true the
      // Apply button is disabled and a progress bar is shown — preventing the
      // user from re-firing the (long) bulk operation by clicking many times.
      // The frontend calls the backend ONCE PER CLIENT so we get real per-
      // client progress (and so one bad client can't kill the rest visually
      // either; the backend also catches per-client failures defensively).
      duplicating: false,
      duplicateDone: 0,
      duplicateTotal: 0,
      duplicateSuccesses: [],
      duplicateFailures: [],
      // Toggle for the post-run report modal. Set to true once a duplicate-
      // to-org run finishes (success or failure); reset by the modal close.
      showDuplicateReport: false,
      options: [
        {
          text: 'Option 1',
          value: 'option1'
        },
        {
          text: 'Option 2',
          value: 'option2'
        },
        {
          text: 'Option 3',
          value: 'option3'
        },
      ],
      file: null,

      // stepper
      currentStep: 1,
      // action selection
      bulkAction: null,
      // pickers (reuse your existing v-models so your API functions work as-is)
      applyError: null,
      //Sequential selected clients index
      sequentialIndex: 0,
    };
  },
  async created() {
    await this.refreshData();
    let filter = ClientFilterFields();
    if (this.$route.meta.departments) {
      this.filter.buTypes = this.$route.meta.departments;
    } else {
      this.filter.buTypes = [];
    }
    this.filterFields = filter;
    this.loading = false;

  },

  computed: {
    ...mapGetters('data', ['allOrganizations', 'allBusinessUnits']),
    // BU options for the duplicate-to-org wizard, filtered to the picked org.
    // Computed locally so we don't depend on BusinessUnitSelectBox's internal
    // validateFilter (which is multi-select-only and was corrupting the
    // single-string v-model in this wizard).
    dupBuOptions() {
      if (!this.dupOrgId) return [];
      return (this.allBusinessUnits || []).filter(bu => bu.orgId === this.dupOrgId);
    },
    ...mapGetters('user', ['isMultiOrgUser']),
    viewKey() {
      return this.$route.name || 'clients';
    },
    isFormValid() {
      return this.selectedOption !== null && this.file !== null;
    },
    bulkActionOptions() {
      let $this = this;
      let options = [
        {
          value: null,
          text: `— ${$this.$t('operations.bulkActions.selectAction')} —`
        },
        {
          value: 'SEQUENTIAL',
          text: $this.$t('operations.bulkActions.showSequentially')
        },
      ]
      if(this.hasPermission(permissions.CLIENT_UPDATE_REPRESENTATIVE)){
        options.push(
            {
              value: 'SET_REP',
              text: $this.$t('operations.bulkActions.setRepresentative')
            },
            {
              value: 'SET_RANDOM_REP',
              text: $this.$t('operations.bulkActions.setRandomRepresentative')
            },
        )
      }
      options.push({
        value: 'CHANGE_STATUS',
        text: $this.$t('operations.bulkActions.setStatus')
      })
      if(this.hasPermission(permissions.CLIENT_UPDATE_BU)){
        options.push({
          value: 'CHANGE_BU',
          text: $this.$t('operations.bulkActions.changeBusinessUnit')
        })
      }
      if(this.hasPermission(permissions.CLIENT_UPDATE_DETAILS)){
        options.push({
          value: 'DELETE_COMMENTS',
          text: $this.$t('operations.bulkActions.deleteComments')
        })
      }
      if(this.isMultiOrgUser){
         options.push({
          value: 'DUPLICATE_TO_ORG',
          text: $this.$t('operations.bulkActions.duplicateToOrg')
        })
      }
      return options;
    },

    actionHint() {
      switch (this.bulkAction) {
        case 'SET_REP':
          return 'Choose a representative, then apply.';
        case 'SET_RANDOM_REP':
          return 'Choose a pool of representatives (multi-select), then apply.';
        case 'CHANGE_STATUS':
          return 'Choose a status, then apply.';
        case 'CHANGE_BU':
          return 'Choose a business unit, then apply.';
        case 'DUPLICATE_TO_ORG':
          return 'Choose a organization, then apply.';
        case 'DELETE_COMMENTS':
          return 'Delete all comments for selected clients. No extra options needed.';
        default:
          return '';
      }
    },
    isReadyToApply() {
      if (!this.bulkAction) return false;

      // DELETE_COMMENTS requires no secondary input
      if (this.bulkAction === 'DELETE_COMMENTS') return true;

      // random rep requires multi selection
      if (this.bulkAction === 'SET_RANDOM_REP') {
        return Array.isArray(this.optionSelectionMulti) && this.optionSelectionMulti.length > 0;
      }

      // duplicate-to-org needs both org and BU; rep / notes / status are optional
      if (this.bulkAction === 'DUPLICATE_TO_ORG') {
        return !!this.dupOrgId && !!this.dupBuId;
      }

      // normal case: representative, status, BU
      return !!this.optionSelection;
    },
    // 0..100 progress for the duplicate-to-org operation. Stays at 0 when
    // not running.
    duplicateProgress() {
      if (!this.duplicateTotal) return 0;
      return Math.floor((this.duplicateDone / this.duplicateTotal) * 100);
    },

  },
  methods: {
    ...mapActions('clients', ['refreshData', 'updateBusinessUnitOfClient', 'duplicateClientToNewOrg', 'duplicateClientToNewOrgWithOptions', 'deleteCommentsOfClients',
      'updateStatusOfClient', 'updateRepresentativeOfClient', 'updateToRandomRep', 'importClientDataExcel']),

    onPickAction() {
      this.applyError = null;
      this.currentStep = this.bulkAction ? 2 : 1;
      // clear previous picks when switching action
      this.optionSelection = null;
      this.optionSelectionMulti = [];
      if(this.bulkAction === 'SEQUENTIAL') {
        this.onClose();
        this.bulkAction = null;
        this.currentStep = 1;
        this.showClientsSequentially();
      }
    },
    resetTargets() {
      this.optionSelection = null;
      this.optionSelectionMulti = [];
      this.applyError = null;
      // duplicate-to-org wizard fields
      this.dupOrgId = null;
      this.dupBuId = null;
      this.dupRepId = null;
      this.dupStatusId = null;
      this.dupCopyNotes = false;
    },
    onDupOrgChange() {
      // Changing org invalidates BU + rep (BU list and user filter both depend
      // on org). Status survives — it's org-scoped server-side anyway.
      this.dupBuId = null;
      this.dupRepId = null;
    },
    onDupBuChange() {
      // Changing BU invalidates rep (rep list is filtered by BU).
      this.dupRepId = null;
    },
    resetBulkAction() {
      this.bulkAction = null;
      this.resetTargets();
      this.currentStep = 1;
    },

    /**
     * Apply orchestrates your existing API functions.
     * It simply routes to your existing handlers so you don't have to rewrite API logic.
     * Ensure the methods below exist in your component already:
     * - onOkRepresentative()
     * - onOkRepresentativeRandom()
     * - onOkStatus()
     * - onOkBU()
     */
    async applyBulkAction() {
      try {
        this.applyError = null;

        if (!this.isReadyToApply) return;
        // Hard guard against double-clicks while a long-running duplicate is
        // in flight. The button is also :disabled, but this is a belt+braces
        // safety net in case it ever fires anyway (rapid clicks, keyboard).
        if (this.duplicating) return;

        switch (this.bulkAction) {
          case 'SET_REP':
            // expects optionSelection bound to user-select-box
            await this.onOkRepresentative();
            break;

          case 'SET_RANDOM_REP':
            // expects optionSelectionMulti bound to user-select-box :multi="true"
            await this.onOkRepresentativeRandom();
            break;

          case 'CHANGE_STATUS':
            // expects optionSelection bound to client-status-picker (or dummy)
            await this.onOkStatus();
            break;

          case 'CHANGE_BU':
            // expects optionSelection bound to business-unit-select-box
            await this.onOkBU();
            break;

          case 'DUPLICATE_TO_ORG':
            // expects optionSelection bound to business-unit-select-box
            await this.onOkOrg();
            break;

          case 'DELETE_COMMENTS':
            await this.onOkDeleteComments();
            break;
        }

        // after success, reset the UI
        this.resetBulkAction();
      } catch (e) {
        this.applyError = e?.message || 'Failed to apply action';
      }
    },

    openColumnsModal() {
      this.$refs['columns-modal'].showModal();
    },
    onClientSelected(selectedId, event) {
      // Check if ctrl key is pressed
      if (event && event.ctrlKey) {
        // Open new tab with /client/:clientId
        const url = `/client/${selectedId}`;
        window.open(url, '_blank');
        return;
      }
      //console.log("onClientSelected", selectedId)
      this.showClient(selectedId);
    },
    showClientsSequentially(){
      this.sequentialIndex = 0;
      let selectedClients = this.$refs['clientTable'].getAllSelectedItems();
      let row =this.$refs['clientTable'].rows;
      //Sort selectedClients based on the order in the table
      selectedClients = selectedClients.sort((a,b) => {
        const indexA = row.findIndex(r => r.id === a.id);
        const indexB = row.findIndex(r => r.id === b.id);
        return indexA - indexB;
      });
      this.$refs['client-modal-basic'].showModal(selectedClients[this.sequentialIndex].id);
    },
    showNextClientsSequentially(){
      this.sequentialIndex += 1;
      const selectedClients = this.$refs['clientTable'].getAllSelectedItems();
      if(this.sequentialIndex < selectedClients.length){
        this.$refs['client-modal-basic'].showModal(selectedClients[this.sequentialIndex].id);
      }else {
        this.$refs['client-modal-basic'].closeModal();
        this.sequentialIndex += 1;
      }
    },
    onClientModalBasicClosed(){
      this.refreshTables();
      this.$refs['clientTable'].clearSelection();
      this.sequentialIndex = 0;
    },
    async updateBusinessUnitIdOfClient() {
      let $this = this;
      let loadingToken = this.$refs['clientTable'].setLoading();

      let cs = this.$refs['clientTable'].getAllSelected();
      let bu = {
        clientsId: Object.keys(cs),
        newBusiUnit: this.optionSelection
      };
      let ans = await this.updateBusinessUnitOfClient(bu);
      this.$refs['clientTable'].setLoaded(loadingToken);
      $this.refreshTables();
    },
    /**
     * Duplicate the currently-selected clients into the chosen org/BU.
     *
     * Calls the backend ONCE PER CLIENT so we can show real per-client
     * progress (% based on done/total) and so a single bad client surfaces
     * as a row in the report instead of aborting the whole batch. The
     * backend ALSO catches per-client failures defensively for callers
     * that pass multi-id batches; we rely on the same response shape
     * { successes, failures } either way.
     *
     * When done, opens the duplicate-report modal listing every success
     * and every failure with its reason. The Apply button is disabled
     * (and `applyBulkAction` returns early) for the whole run.
     */
    async duplicateClientToOrg() {
      const cs = this.$refs['clientTable'].getAllSelected();
      // ClientsTable seeds `selected` with `{ all: false }` for the
      // header "select-all" checkbox, so Object.keys(cs) includes the
      // literal "all" key. Strip it (and any falsy entries) — otherwise
      // the loop fires an extra "all" iteration that the backend can't
      // resolve and the report shows a phantom failure row.
      const clientIds = Object.keys(cs).filter(id => id !== 'all' && cs[id]);
      if (!clientIds.length) return;

      const loadingToken = this.$refs['clientTable'].setLoading();

      // Reset + flip the duplicating flag BEFORE the first await so the
      // button disables synchronously — protects against double-clicks.
      this.duplicating = true;
      this.duplicateTotal = clientIds.length;
      this.duplicateDone = 0;
      this.duplicateSuccesses = [];
      this.duplicateFailures = [];
      this.applyError = null;

      const baseReq = {
        orgId: this.dupOrgId,
        buId: this.dupBuId,
        representativeUserId: this.dupRepId,
        copyNotes: this.dupCopyNotes,
        statusId: this.dupStatusId,
      };

      try {
        for (const id of clientIds) {
          try {
            const res = await this.duplicateClientToNewOrgWithOptions({
              ...baseReq,
              clientIds: [id],
            });
            // Backend returns { successes: Client[], failures: {...}[] }.
            // For a single-id call either array has at most one entry, but
            // we spread defensively in case the shape ever changes.
            if (res && Array.isArray(res.successes)) {
              this.duplicateSuccesses.push(...res.successes);
            }
            if (res && Array.isArray(res.failures)) {
              this.duplicateFailures.push(...res.failures);
            }
          } catch (perClientErr) {
            // The whole HTTP call failed (network, 4xx/5xx from global
            // validation, etc.). Record it as a failure for this id so
            // the rest of the batch keeps going.
            const reason = this.getErrorDescription
                ? this.getErrorDescription(perClientErr)
                : (perClientErr?.message || 'Request failed');
            this.duplicateFailures.push({
              clientId: id,
              email: cs[id]?.email || null,
              reason,
            });
          } finally {
            this.duplicateDone += 1;
          }
        }

        // Summary toast — full breakdown lives in the report modal.
        const okCount = this.duplicateSuccesses.length;
        const failCount = this.duplicateFailures.length;
        if (failCount === 0) {
          this.showSuccessNotification(
              this.$t('globalMessages.addDataSuccess', { system: this.$t('menu.clients') }));
        } else if (okCount === 0) {
          this.showErrorNotification(
              this.$t('clientsBulk.duplicateReport.title'),
              this.$t('clientsBulk.duplicateReport.allFailed', { count: failCount }));
        } else {
          this.showSuccessNotification(
              this.$t('clientsBulk.duplicateReport.partial', { ok: okCount, failed: failCount }));
        }
      } finally {
        this.duplicating = false;
        this.$refs['clientTable'].setLoaded(loadingToken);
        this.refreshTables();
        // Open the report AFTER unlocking the UI so the user can dismiss it
        // and immediately interact with the table.
        this.showDuplicateReport = true;
      }
    },
    updateRepresentativeClient() {
      let $this = this;
      let loadingToken = this.$refs['clientTable'].setLoading();

      let cs = this.$refs['clientTable'].getAllSelected();
      let body = {
        clientsId: Object.keys(cs),
        newRepresentative: this.optionSelection
      };
      this.updateRepresentativeOfClient(body)
          .then(() => {
                console.log('after the update RepresentativeOfClient');
                this.$refs['clientTable'].setLoaded(loadingToken);
                $this.refreshTables();
              }, err => {
                console.log('there are error in updating the representative in viewFile', err);
              }
          );
    },
    updateStatusClient() {
      let $this = this;
      let loadingToken = this.$refs['clientTable'].setLoading();

      let cs = this.$refs['clientTable'].getAllSelected();
      let statusClient = {
        clientsId: Object.keys(cs),
        newStatus: this.optionSelection
      };
      console.log('client status', statusClient);
      this.updateStatusOfClient(statusClient)
          .then(() => {
                console.log('after the update StatusOfClient');
                this.$refs['clientTable'].setLoaded(loadingToken);
                $this.refreshTables();
              }
          );
    },
    onShow() {
      // This is called just before the popover is shown
      // Reset our popover form variables
      this.optionSelection = '';
      this.closeAllPopovers();
    },
    onClose() {
      this.popoverShowBU = false;
      this.popoverShowStatus = false;
      this.popoverShowRepresentative = false;
      this.popoverShowRepresentativeRandom = false;
    },

    onOkBU() {
      if (this.optionSelection === '') {
        console.log('the business unit is empty');
        this.onClose();
      }

      this.updateBusinessUnitIdOfClient();
      this.optionSelection = '';
      this.onClose();
    },
    onOkOrg() {
      // Wizard fields are validated by isReadyToApply (org + BU required).
      this.duplicateClientToOrg();
      this.onClose();
    },
    onOkStatus() {
      if (this.optionSelection === '') {
        console.log('the status is empty');
        this.onClose();
      }

      this.updateStatusClient();
      this.optionSelection = '';
      this.onClose();
    },
    onOkRepresentative() {
      if (this.optionSelection === '') {
        console.log('the representative is empty');
        this.onClose();
      }

      this.updateRepresentativeClient();
      this.optionSelection = '';
      this.onClose();
    },
    onOkRepresentativeRandom() {
      if (this.optionSelectionMulti == null) {
        console.log('the representative is empty');
        this.onClose();
      }

      this.updateRepresentativeClientRandomly();
      this.optionSelectionMulti = null;
      this.onClose();

    },
    async onOkDeleteComments() {
      const selected = this.$refs['clientTable'].getAllSelected();
      const body = {
        clientsId: Object.keys(selected)
      };

      try {
        let loadingToken = this.$refs['clientTable'].setLoading();

        // ADD YOUR VUEX ACTION NAME HERE
        await this.deleteCommentsOfClients(body);

        this.$refs['clientTable'].setLoaded(loadingToken);
        this.refreshTables();
      } catch (err) {
        console.error('Error deleting comments:', err);
        throw err;
      }
    }
,
    updateRepresentativeClientRandomly() {
      let $this = this;
      let loadingToken = this.$refs['clientTable'].setLoading();

      let cs = this.$refs['clientTable'].getAllSelected();
      let userRepRequest = {
        clientsId: Object.keys(cs),
        repIds: this.optionSelectionMulti
      };
      console.log('user rep reqest', userRepRequest);
      this.updateToRandomRep(userRepRequest)
          .then(() => {
                console.log('after the update RepresentativeOfClient');
                this.$refs['clientTable'].setLoaded(loadingToken);
                $this.refreshTables();
              }, err => {
                console.log('there are error in updating the representative in viewFile', err);
              }
          );
    },
    createExcelFile() {
      this.$refs['clientTable'].createExcelFile();
    },
    showImportExcelModal() {
      this.selectedBranchExcel = null;
      this.selectedUserExcel = null;
      this.$refs.fileUploadModal.show();
    },
    downloadExcelTemplateTemplate() {

    },
    downloadExcelTemplate() {
      // Specify the path to your Excel file in the public directory
      const fileUrl = '/clientTemplate.xlsx';
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = 'clientTemplate.xlsx'; // Optional: specify the download file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async importExcelFile() {
      let $this = this;
      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('table', 'Client');

      // Build params object
      const params = {
        branchId: this.selectedBranchExcel,
      };

      // 👉 Add userId ONLY if selectedUserExcel is not null/empty
      if (this.selectedUserExcel && this.selectedUserExcel !== '') {
        params.userId = this.selectedUserExcel;
      }

      // Encode to base64
      formData.append('params', btoa(JSON.stringify(params)));

      try {
        let ans = await this.importClientDataExcel({ formData: formData });

        this.showSuccessNotification(
            $this.$t('globalMessages.addDataSuccess', {
              system: $this.$t('menu.clients'),
            })
        );

        this.refreshTables();

      } catch (err) {
        this.showServerErrorNotification(
            $this.$t('addDataError', {
              system: $this.$t('menu.clients'),
              error: err,
            })
        );
      }

      this.$refs.fileUploadModal.hide();
      this.refreshTables();
    }
    ,

  }
};
</script>

<style>
.style-chooser .vs__search::placeholder,
.style-chooser .vs__dropdown-toggle,
.style-chooser .vs__dropdown-menu {
  background: #dfe5fb;
  border: none;
  color: #394066;
  text-transform: lowercase;
  font-variant: small-caps;
  height: 10px;
}


.client-action-button {
  width: 220px;
  overflow: hidden;
  white-space: nowrap;
  transition: all 0.5s ease-out;
}


.popover.no-arrow > .arrow {
  display: none;
}

.min-w-220 {
  min-width: 220px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .15s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

</style>
